import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import {
  BlotterTable,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  FormControlSizes,
  IconName,
  TRADE_ALLOCATION,
  WSBlotterTableMaxRecordsReachedWarning,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useDateRangeFilter,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type FilterClause,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { columns } from './columns';
import { extractAllocationsPipe } from './extractAllocationsPipe';
import type { TradeAllocationsBlotterTableProps } from './types';
import { onlyServerFilterKeys, useTradeAllocationFilter } from './useTradeAllocationFilter';
import { useTradeAllocationMenu } from './useTradeAllocationMenu';

export function TradeAllocationsBlotterTable({
  orderID,
  blotterID,
  blotterPersistID = blotterID,
  defaultColumns = columns,
  defaultFilter,
  persistFilter,
  initialIsOpen,
  onCloneTab,
  showCloneTab,
}: TradeAllocationsBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();

  const persisted = usePersistedBlotterTable(blotterPersistID, { filter: defaultFilter }, { persistFilter });

  const tradeAllocationFilter = useTradeAllocationFilter({
    initialFilter: persisted.initialState?.filter,
    saveFilter: persisted.onFilterChanged,
  });
  const { clientSideFilter, changeFilter, filterableProperties, initialFilterClauses } = tradeAllocationFilter;

  // Forward the parts of the filter being sent to the backend
  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters({
          ...curr,
          MarketAccounts: filterClausesByPropertyKey.get('MarketAccounts')?.selections,
          Sides: filterClausesByPropertyKey.get('Sides')?.selections,
          Statuses: filterClausesByPropertyKey.get('Statuses')?.selections,
          Symbols: filterClausesByPropertyKey.get('Symbols')?.selections,
          Users: filterClausesByPropertyKey.get('Users')?.selections,
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const accordionFilterBuilder = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const {
    getContextMenuItems,
    column: menuColumn,
    dialogs,
  } = useTradeAllocationMenu({
    filterableProperties,
    openClause: accordionFilterBuilder.openClause,
  });

  const columns = useMemo(() => [...defaultColumns, menuColumn], [defaultColumns, menuColumn]);

  const request = useMemo(
    () => ({
      name: TRADE_ALLOCATION,
      tag: blotterID,
      Throttle: '1s',
      ...(orderID != null && { OrderID: orderID }),
      ...removeEmptyFilters(onlyServerFilterKeys(tradeAllocationFilter.filter)),
    }),
    [blotterID, orderID, tradeAllocationFilter.filter]
  );

  // This call owns the true filter state
  const blotterTable = useWsBlotterTable({
    request,
    rowID: 'id',
    clientLocalFilter: clientSideFilter,
    pipe: extractAllocationsPipe,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    columns,
    persistence: persisted,
    gridOptions: { rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS, getContextMenuItems },
  });

  const dateRangeFilter = useDateRangeFilter(tradeAllocationFilter.filter, changeFilter);

  const handleCloneTab = useCallback(() => {
    onCloneTab?.();
  }, [onCloneTab]);

  return (
    <>
      <BlotterTableFilters
        {...accordionFilterBuilder}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        prefix={
          showCloneTab && (
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleCloneTab}
            >
              Clone Tab
            </Button>
          )
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      <BlotterTable {...blotterTable} />
      {dialogs}
    </>
  );
}
